/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export enum ScrollDirection {
  Up,
  Down,
}

export default class ScrollDirectionListener {
  private isScrolling = false;
  private setIsScrolling: () => void;
  private lastScrollTop = 0;
  private curDirection?: ScrollDirection;
  private handleScrollInterval: NodeJS.Timer | null;

  constructor(private readonly onChange: (newDirection: ScrollDirection) => void) {
    // Initialize the listener for window.addEventListener('scroll). This correctly bind this
    // listener as "this" (otherwise "this" will be the "window" object).
    this.setIsScrolling = () => (this.isScrolling = true);
    window.addEventListener('scroll', this.setIsScrolling);

    this.handleScrollInterval = setInterval(() => {
      if (this.isScrolling) {
        this.hasScrolled();
        this.isScrolling = false;
      }
    }, 250);
  }

  public destroy() {
    if (this.handleScrollInterval) {
      clearInterval(this.handleScrollInterval);
      this.handleScrollInterval = null;
    }
    window.removeEventListener('scroll', this.setIsScrolling);
  }

  private hasScrolled() {
    let newDirection: ScrollDirection;
    const scrollTop = window.scrollY;
    if (scrollTop > this.lastScrollTop) {
      newDirection = ScrollDirection.Up;
    } else {
      newDirection = ScrollDirection.Down;
    }

    this.lastScrollTop = scrollTop;
    if (newDirection !== this.curDirection) {
      this.curDirection = newDirection;
      this.onChange(newDirection);
    }
  }
}
